import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Drawer,
  Select,
  DatePicker,
  Radio,
  Row,
  Col,
  Space,
  Tooltip,
} from "antd";
import { PlusOutlined, InfoCircleOutlined, RightOutlined } from "@ant-design/icons";

import moment from "moment";

import { getRealSiips as GetSurgicalInterventionRealSiips } from "../../api/SurgicalIntervention";
import { create as CreateSurgicalIntervention } from "../../api/SurgicalIntervention";
import { update as UpdateSurgicalIntervention } from "../../api/SurgicalIntervention";

import { siips_values, deferredOutputReasons } from "../../constants";

import { formatFirstname, formatLastname } from '../../utils';

const { Option } = Select;

const layout = {
  labelCol: {
    span: 6,
  },
  wrapperCol: {
    span: 16,
    className: "mx-3",
  },
};

const layoutWithoutLabel = {
  wrapperCol: {
    span: 24,
    className: "mx-2"
  },
};

const layoutSiips = {
  labelCol: {
    span: 14,
  },
  wrapperCol: {
    span: 6,
    className: "mx-3"
  },
};

const layoutInputSiips = {
  style: { width: 80 },
  placeholder: 1,
};

const labelLayout = {
  style: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    height: 32,
    color: "rgba(0, 0, 0, 0.85)",
    fontSize: 14,
  },
};

const layoutSelect = {
  showSearch: true,
  allowClear: true,
  optionFilterProp: "children",
};

const layoutAsyncSelect = {
  ...layoutSelect,
  filterOption: false,
  defaultActiveFirstOption: false,
};

const SurgicalInterventionEndedForm = (props) => {
  const { surgicalIntervention } = props;
  const [visible, setVisible] = useState(false);
  const [patient, setPatient] = useState(null);
  const [surgicalProcedure, setSurgicalProcedure] = useState(null);
  const [age, setAge] = useState(null);
  const [expectedEndDuration, setExpectedEndDuration] = useState(null);
  const [
    showDeferredOutputCustomReasonInput,
    setShowDeferredOutputCustomReasonInput,
  ] = useState(false);
  const [showDeferredOutputReasonInput, setShowDeferredOutputReasonInput] =
    useState(false);
  const [endDefaultValues, setEndDefaultValues] = useState(false);
  const [form] = Form.useForm();

  const onFinish = () => {
    let values = form.getFieldsValue();
    storeSurgicalIntervention(values).then(() => {
      props.onOut();
      form.resetFields();
    });
  };

  const onClose = () => {
    form.resetFields();
    props.onClose();
  };

  /**
   *
   * @param {*} values
   * @returns
   */
  const storeSurgicalIntervention = (values) => {
    let {
      end_at,
      output_type,
      deferred_output_reason,
      deferred_output_custom_reason,
    } = values;

    let newSurgicalIntervention =
      surgicalIntervention !== null ? { ...surgicalIntervention } : {};

    newSurgicalIntervention.output_type = output_type;
    newSurgicalIntervention.deferred_output_reason = deferred_output_reason;
    newSurgicalIntervention.deferred_output_custom_reason =
      deferred_output_custom_reason;
    newSurgicalIntervention.end_at = end_at.startOf("day").toISOString(true);

    newSurgicalIntervention.real_siips = {
      base: values.real_siips_base,
      technical: values.real_siips_technical,
      relational: values.real_siips_relational,
    };

    if (surgicalIntervention) {
      return UpdateSurgicalIntervention(
        surgicalIntervention.id,
        newSurgicalIntervention
      );
    }
    return CreateSurgicalIntervention(newSurgicalIntervention);
  };

  /**
   *
   * @param {*} errorInfo
   */
  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  /**
   *
   * @param {*} startAt
   */
  const onChangeStartAtHandler = (startAt) => {
    if (startAt !== null) {
      let expectedEndAt = defineExpectedEndAt(startAt);
      showExpectedEndDuration(startAt, expectedEndAt);
      form.setFieldsValue({
        expected_end_at: expectedEndAt,
      });
    }
  };

  /**
   *
   * @param {*} v
   */
  const onChangeDeferredOutputCustomReasonInput = (v) => {
    setShowDeferredOutputCustomReasonInput(v === 5);
  };

  /**
   *
   * @param {*} v
   */
  const onChangeEndAtHandler = (v) => {
    let expected_end_at = form.getFieldValue("expected_end_at");
    showShowDeferredOutputReasonInput(expected_end_at, v);
  };

  /**
   *
   * @param {*} expected_end_at
   * @param {*} end_at
   */
  const showShowDeferredOutputReasonInput = (expected_end_at, end_at) => {
    if (surgicalIntervention && surgicalIntervention.rescheduled) {
        setShowDeferredOutputReasonInput(true);
        return;
    }

    if (expected_end_at === null || end_at === null) {
      setShowDeferredOutputReasonInput(false);
      return;
    }

    expected_end_at.startOf("days");
    end_at.startOf("days");
    const isDiff = end_at.diff(expected_end_at);
    setShowDeferredOutputReasonInput(isDiff);
  };

  /**
   *
   * @param {*} birthdate
   */
  const showAge = (birthdate) => {
    let age = moment().diff(birthdate, "years");
    setAge(age);
  };

  /**
   *
   * @param {*} startEndAt
   * @param {*} expectedEndAt
   */
  const showExpectedEndDuration = (startEndAt, expectedEndAt) => {
    let duration = expectedEndAt.diff(startEndAt, "days");
    setExpectedEndDuration(duration);
  };

  /**
   *
   * @param {*} startEndAt
   * @returns
   */
  const defineExpectedEndAt = (startEndAt) => {
    // @todo doit varier selon le type de procédure
    return moment(startEndAt).add(7, "days");
  };

  useEffect(() => {
    setVisible(props.visible);
  }, [props.visible]);

  let initialStartAt = moment();
  let initialValues = {
    patient_id: null,
    patient_birthdate: moment().subtract(10, "years"),
    planned: true,
    start_at: initialStartAt,
    expected_end_at: defineExpectedEndAt(initialStartAt),
    end_at: moment(),
    expected_siips_base: 0,
    expected_siips_technical: 0,
    expected_siips_relational: 0,
    real_siips_base: 0,
    real_siips_technical: 0,
    real_siips_relational: 0,
    output_type: "home",
    deferred_output_custom_reason: null,
    deferred_output_reason: null,
  };

  useEffect(async () => {
    let hasEndDefaultValues = false;
    if (surgicalIntervention) {
      const endDefaultValues = await GetSurgicalInterventionRealSiips(
        surgicalIntervention.id
      );

      let { patient, surgical_procedure } = surgicalIntervention;
      initialValues.patient_id = patient.id;
      initialValues.patient_birthdate = moment(patient.birthdate);
      initialValues.surgical_procedure_id = surgical_procedure.id;
      setPatient(patient);
      setSurgicalProcedure(surgical_procedure);

      initialValues.start_at = moment(surgicalIntervention.start_at);
      initialValues.expected_end_at = moment(
        surgicalIntervention.expected_end_at
      );
      if (surgicalIntervention.end_at !== null) {
        initialValues.end_at = moment(surgicalIntervention.end_at);
      }

      initialValues.planned = surgicalIntervention.planned;
      initialValues.output_type = surgicalIntervention.output_type;

      let { expected_siips, real_siips } = surgicalIntervention;
      let { real_siips: realSiipsAuto } = endDefaultValues;

      initialValues.expected_siips_base = expected_siips.base;
      initialValues.expected_siips_technical = expected_siips.technical;
      initialValues.expected_siips_relational = expected_siips.relational;

      if (real_siips.base > 0) {
        initialValues.real_siips_base = real_siips.base;
      } else if (realSiipsAuto.base > 0) {
        initialValues.real_siips_base = realSiipsAuto.base;
        hasEndDefaultValues = true;
      } else {
        initialValues.real_siips_base = expected_siips.base;
      }

      if (real_siips.technical > 0) {
        initialValues.real_siips_technical = real_siips.technical;
      } else if (realSiipsAuto.technical > 0) {
        initialValues.real_siips_technical = realSiipsAuto.technical;
        hasEndDefaultValues = true;
      } else {
        initialValues.real_siips_technical = expected_siips.technical;
      }

      if (real_siips.base > 0) {
        initialValues.real_siips_relational = real_siips.relational;
      } else if (realSiipsAuto.relational > 0) {
        initialValues.real_siips_relational = realSiipsAuto.relational;
        hasEndDefaultValues = true;
      } else {
        initialValues.real_siips_relational = expected_siips.relational;
      }

      let { deferred_output_custom_reason, deferred_output_reason } =
        surgicalIntervention;
      initialValues.deferred_output_custom_reason =
        deferred_output_custom_reason;
      initialValues.deferred_output_reason = deferred_output_reason;
    }

    form.setFieldsValue(initialValues);
    showAge(initialValues.patient_birthdate);
    showShowDeferredOutputReasonInput(
      initialValues.expected_end_at,
      initialValues.end_at
    );
    showExpectedEndDuration(
      initialValues.start_at,
      initialValues.expected_end_at
    );
    setEndDefaultValues(hasEndDefaultValues);
    setShowDeferredOutputCustomReasonInput(
      initialValues.deferred_output_reason === 5
    );
  }, [surgicalIntervention]);

  let patientOptions = [];
  if (patient) {
    patientOptions.push(
      <Option key={0} value={patient.id}>
      {[formatFirstname(patient.firstname), formatLastname(patient.lastname)].join(' ')}
      </Option>
    );
  }

  let surgicalProcedureOptions = [];
  if (surgicalProcedure) {
    surgicalProcedureOptions.push(
      <Option key={0} value={surgicalProcedure.id}>
        {surgicalProcedure.name}
      </Option>
    );
  }

  let deferredOutputReasonsOptions = deferredOutputReasons.map((option) => (
    <Option value={option.id}>{option.label}</Option>
  ));

  let chargeOptions = siips_values.map((option, index) => (
    <Option key={index} value={option}>
      {option}
    </Option>
  ));

  let deferredOutputCustomReasonInput = null;

  if (showDeferredOutputCustomReasonInput) {
    deferredOutputCustomReasonInput = (
      <Form.Item
        label="Raison personnalisée"
        name="deferred_output_custom_reason"
      >
        <Input
          className="ant-input-text"
          placeholder="Saisissez une raison personnalisée"
        />
      </Form.Item>
    );
  }

  let deferredOutputCustomInput = null;

  if (showDeferredOutputReasonInput) {
    deferredOutputCustomInput = (
      <>
        <Form.Item
          label="Raison de sortie différée"
          name="deferred_output_reason"
        >
          <Select
            {...layoutSelect}
            placeholder="Sélectionnez une raison"
            optionFilterProp="children"
            onChange={onChangeDeferredOutputCustomReasonInput}
          >
            {deferredOutputReasonsOptions}
          </Select>
        </Form.Item>
        {deferredOutputCustomReasonInput}
      </>
    );
  }

  /**
   *
   * @param {*} current
   * @returns
   */
  let disabledEndDate = (current) => {
    let startAt = form.getFieldValue("start_at");
    return current && current < startAt;
  };

  let rescheduledValue = (surgicalIntervention && surgicalIntervention.rescheduled) ? "Oui" : "Non";

  return (
    <Drawer
      title="Sortie d'un patient"
      placement="left"
      closable={true}
      onClose={onClose}
      visible={visible}
      width="800px"
      key="left"
      footer={
        <div
          style={{
            textAlign: "right",
          }}
        >
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Annuler
          </Button>
          <Button onClick={onFinish} type="primary">
            Enregistrer
          </Button>
        </div>
      }
    >
      <Form
        {...layout}
        form={form}
        name="care_unit"
        initialValues={initialValues}
        onFinishFailed={onFinishFailed}
      >
        <Row>
          <Col span={6} style={{ textAlign: "right" }}>
            <label
              {...labelLayout}
              htmlFor="surgical_intervention_patient_id"
              title="Patient"
            >
              Patient
              <span style={{ margin: "0 8px 0 2px" }}>:</span>
            </label>
          </Col>
          <Col span={18}>
            <Space className="mx-3">
              <Tooltip placement="bottom">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{ marginBottom: 24 }}
                  disabled={true}
                />
              </Tooltip>
              <Form.Item name="patient_id">
                <Select
                  {...layoutAsyncSelect}
                  style={{ width: 450 }}
                  disabled={true}
                >
                  {patientOptions}
                </Select>
              </Form.Item>
            </Space>
          </Col>
        </Row>
        <Form.Item label="Âge">
          <span>
            {moment(form.getFieldValue("birthdate")).format("DD/MM/YYYY")} (
            {age} ans)
          </span>
        </Form.Item>
        <Form.Item label="Programmé ?" name="planned">
          <Radio.Group>
            <Radio value={true} disabled={true}>
              Oui
            </Radio>
            <Radio value={false} disabled={true}>
              Non
            </Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="Procédure chirurgicale" name="surgical_procedure_id">
          <Select
            {...layoutAsyncSelect}
            placeholder="Sélectionnez une procedure"
            disabled={true}
          >
            {surgicalProcedureOptions}
          </Select>
        </Form.Item>
        <Form.Item label="Date d'entrée" name="start_at">
          <DatePicker
            format="DD/MM/YYYY"
            onChange={onChangeStartAtHandler}
            disabled={true}
          />
        </Form.Item>
        <Form.Item label="Durée moyenne du séjour">
          <span>{expectedEndDuration} jours</span>
        </Form.Item>
        <Form.Item label="Date de sortie estimée" name="expected_end_at">
          <DatePicker format="DD/MM/YYYY" disabled={true} />
        </Form.Item>
        <Form.Item
          label="Reporter ?"
          name="rescheduled"
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: "Les dates du séjour ont elles été modifiées ?"
          }}
        >
          {rescheduledValue}
        </Form.Item>
        <Form.Item label="Date de sortie réelle" name="end_at">
          <DatePicker
            format="DD/MM/YYYY"
            onChange={onChangeEndAtHandler}
            disabledDate={disabledEndDate}
          />
        </Form.Item>
        {deferredOutputCustomInput}
        <Form.Item label="Mode de sortie" name="output_type">
          <Radio.Group>
            <Radio value={"home"}>Domicile</Radio>
            <Radio value={"convalescence"}>Convalescence</Radio>
            <Radio value={"had"}>H.A.D.</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item {...layoutWithoutLabel}>
          <Row>
            <Col className="gutter-row" span={10}>
              <Form.Item>
                <span className="ant-form-text" style={{ fontWeight: "bold" }}>
                  Soins quotidiens moyens estimés
                </span>
              </Form.Item>
              <Form.Item
                label="Soins de base"
                name="expected_siips_base"
                {...layoutSiips}
              >
                <Select
                  disabled={true}
                  {...layoutSelect}
                  {...layoutInputSiips}
                >
                  {chargeOptions}
                </Select>
              </Form.Item>
              <Form.Item
                label="Soins techniques"
                name="expected_siips_technical"
                {...layoutSiips}
              >
                <Select
                  disabled={true}
                  {...layoutSelect}
                  {...layoutInputSiips}
                >
                  {chargeOptions}
                </Select>
              </Form.Item>
              <Form.Item
                label="Soins relationnels"
                name="expected_siips_relational"
                {...layoutSiips}
              >
                <Select
                  disabled={true}
                  {...layoutSelect}
                  {...layoutInputSiips}
                >
                  {chargeOptions}
                </Select>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={1}>
              <Form.Item />
              <Form.Item {...layoutWithoutLabel}>
                <RightOutlined style={{ color: "#bdbdbd" }} />
              </Form.Item>
              <Form.Item {...layoutWithoutLabel}>
                <RightOutlined style={{ color: "#bdbdbd" }} />
              </Form.Item>
              <Form.Item {...layoutWithoutLabel}>
                <RightOutlined style={{ color: "#bdbdbd" }} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={13}>
              <Form.Item>
                <span className="ant-form-text mx-4" style={{ fontWeight: "bold" }}>
                  Soins réels
                  {endDefaultValues ? (
                    <small className="ml-2">
                      (calculé depuis les plans de soins enregitrés)
                    </small>
                  ) : null}
                </span>
              </Form.Item>
              <Form.Item name="real_siips_base" {...layoutSiips}>
                <Select {...layoutSelect} {...layoutInputSiips}>
                  {chargeOptions}
                </Select>
              </Form.Item>
              <Form.Item name="real_siips_technical" {...layoutSiips}>
                <Select {...layoutSelect} {...layoutInputSiips}>
                  {chargeOptions}
                </Select>
              </Form.Item>
              <Form.Item name="real_siips_relational" {...layoutSiips}>
                <Select {...layoutSelect} {...layoutInputSiips}>
                  {chargeOptions}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default SurgicalInterventionEndedForm;
